import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import LockOutlineIcon from '@mui/icons-material/LockOutline'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button } from '~/components/Atoms/Button'
import { Alert } from '~/components/Molecules/Alert'
import { Input } from '~/components/Molecules/Input'
import { HOST, NAV_LINKS, RECAPTCHA_SITE_KEY } from '~/constants'

import logo from '~/assets/logo.svg'
import ReactGA from '~/lib/analytics'
import { useLogin, useSSOLogin, useUser } from '~/lib/auth'

export function Login() {
  const theme = useTheme()
  const login = useLogin()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { mutate: ssoSignIn, data, error: ssoError } = useSSOLogin()

  const user = useUser()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [emailError, setEmailError] = useState('')
  const [useSSO, setUseSSO] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSignInSuccess, setSignInSuccess] = useState(false)
  const [isSSOSignInSuccess, setSSOSignInSuccess] = useState(false)
  const [isLoadingEnded, setLoadingEnded] = useState(false)
  const [error, setError] = useState('')
  const [errorDescription, setErrorDescription] = useState('')
  const [errorLink, setErrorLink] = useState<string | null>(null)
  const [recaptcha, setRecaptcha] = useState<string | null>(null)

  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const verifyEmail = () => {
    if (email === '' || email === null) {
      return
    }
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      setEmailError(t('login:error:invalidEmail'))
    }
  }

  const onSignIn = () => {
    setIsLoading(true)
    login.mutate(
      { email, password, recaptcha },
      {
        onSuccess: (data) => {
          ReactGA.set({ userId: data?.anonymous_uuid, company_id: data?.company_id, user_email: data?.email })
          ReactGA.event({ action: 'user_login', category: 'engagement', label: 'login' })
          setSignInSuccess(true)
        },
        onError: (error) => {
          // Handle login error
          setIsLoading(false)
          if (error?.response?.status === 400) {
            setError(t('login:invalidAuthorization'))
            setErrorDescription(t('login:goToMoodwork'))
            setErrorLink(HOST + '/users/sign_in')
            return
          }
          setError(error?.response?.data?.error_description?.split('. ')[0] ?? t('login:defaultError'))
        },
      }
    )
  }

  useEffect(() => {
    if (user.data) {
      navigate(NAV_LINKS.HOME.to)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        setLoadingEnded(true)
      }, 300)
      return () => clearTimeout(timeout)
    }
  }, [isLoading])

  useEffect(() => {
    if (isSignInSuccess && isLoadingEnded) {
      navigate(NAV_LINKS.HOME.to)
    }
  }, [isSignInSuccess, isLoadingEnded, navigate])

  const onSSOSignIn = () => {
    setIsLoading(true)
    ssoSignIn(email)
  }

  useEffect(() => {
    if (data) {
      setSSOSignInSuccess(true)
    }
    if (ssoError) {
      setIsLoading(false)
      setLoadingEnded(false)
      setError(ssoError?.response?.data?.error_description?.split('. ')[0] ?? t('login:defaultError'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ssoError])

  useEffect(() => {
    if (isSSOSignInSuccess && isLoadingEnded) {
      if (data && data.url) {
        ReactGA.event({ action: 'user_login_sso', category: 'engagement', label: 'login_sso' })
        window.location.href = HOST + data.url
      } else {
        setIsLoading(false)
      }
    } else if (error && isLoadingEnded) {
      setIsLoading(false)
    }
  }, [isSSOSignInSuccess, isLoadingEnded, navigate, data, error])

  useEffect(() => {
    recaptchaRef.current?.execute()
  }, [recaptchaRef])

  return (
    <Box
      display="flex"
      justifyContent="flex-end"
      alignItems="center"
      sx={{
        background: `linear-gradient(133.89deg, ${theme.palette.gradient.mainLight} -1.31%, ${theme.palette.gradient.mainDark} 74.51%)`,
      }}
      height="100vh"
    >
      <Box
        display="flex"
        flexDirection="column"
        width={650}
        justifyContent="space-between"
        gap={theme.spacing['3XL']}
        padding={`${theme.spacing['3XL']} ${theme.spacing.XXL}`}
        bgcolor="white"
        height="100vh"
      >
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <img width={theme.size[22]} height={theme.size[18]} src={logo} alt="logo" />
          <a href={HOST + '/users/sign_in'} target="_blank" rel="noreferrer">
            <Button variant="outlined" label={t('common:moodworkButton')} endIcon={<OpenInNewIcon />} />
          </a>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          gap={theme.spacing.XXL}
          padding={`0 ${theme.spacing['8XL']} 0 ${theme.spacing['8XL']}`}
          justifyContent="center"
        >
          <Alert
            label={error}
            description={errorDescription}
            variant="error"
            link={errorLink}
            onClose={() => {
              setError('')
              setErrorDescription('')
              setErrorLink('')
            }}
          />
          <Typography variant="h2">{t('login:title')}</Typography>
          <Typography variant="body">{useSSO ? t('login:ssoSubtitle') : t('login:subtitle')}</Typography>
          <Box display="flex" flexDirection="column">
            <Input
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
              }}
              label={t('login:email')}
              placeholder={t('login:emailPlaceholder')}
              startIcon={<MailOutlineIcon />}
              inputProps={{ onBlur: verifyEmail, onFocus: () => setEmailError('') }}
              helperText={emailError}
              error={emailError !== ''}
            />
            {!useSSO && (
              <Input
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                }}
                label={t('login:password')}
                placeholder={t('login:passwordPlaceholder')}
                startIcon={<LockOutlineIcon />}
                type="password"
              />
            )}
          </Box>
          <Box display="flex" flexDirection="column" gap={theme.spacing.L}>
            <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_SITE_KEY} onChange={setRecaptcha} />
            <Button
              loading={isLoading}
              loadingPosition="end"
              disabled={
                useSSO
                  ? email === '' || emailError !== '' || isLoading
                  : email === '' || password === '' || emailError !== '' || isLoading
              }
              variant="contained"
              label={t('login:signin')}
              endIcon={!isLoading && <ExitToAppIcon />}
              onClick={() => {
                useSSO ? onSSOSignIn() : onSignIn()
              }}
            />
            <Typography textAlign="center" variant="body">
              ou
            </Typography>
            <Button
              variant="outlined"
              label={useSSO ? t('login:withoutSso') : t('login:sso')}
              onClick={() => {
                setUseSSO(!useSSO)
              }}
            />
          </Box>
          <Link
            href={HOST + '/reset_password'}
            sx={{ cursor: 'pointer' }}
            textAlign="center"
            variant="body"
            color={theme.palette.text.main}
          >
            {t('login:lostPassword')}
          </Link>
        </Box>
        <Box display="flex" justifyContent="flex-end" alignItems="flex-end">
          <Typography variant="caption">@ Moodwork 2025</Typography>
        </Box>
      </Box>
    </Box>
  )
}
